import type { ProductVariantSearchResultDto } from '@/apis/backend/generated/models';

import { useMemo, useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  v1ProductControllerListProducts,
  v1ProductControllerSearchProducts,
} from '@/apis/backend/generated/products/products';

/** Rows per request. One page should overflow the shortest picker so the sentinel starts below the fold. */
export const PRODUCT_PICKER_PAGE_SIZE = 24;

/** A query shorter than this browses the catalog instead of searching it. */
const MIN_SEARCH_LENGTH = 2;

interface UsePaginatedProductsOptions {
  orgId: string;
  clientId: string;
  searchQuery?: string;
  productType?: string;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Offset-paginated product catalog for the pickers, browsing and searching
 * through one infinite query. Both pickers previously took a single fixed page
 * (12 and 20 rows) with no way to reach anything past it, so a product outside
 * the newest page was unreachable unless the user already knew its name.
 *
 * Browse and search are separate endpoints but share a query, so switching
 * between them resets pagination through the query key rather than by hand.
 */
export function usePaginatedProducts({
  orgId,
  clientId,
  searchQuery = '',
  productType = '',
  pageSize = PRODUCT_PICKER_PAGE_SIZE,
  enabled = true,
}: UsePaginatedProductsOptions) {
  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length >= MIN_SEARCH_LENGTH;

  const query = useInfiniteQuery({
    queryKey: isSearching
      ? ([
          `/api/v1/organizations/${orgId}/clients/${clientId}/products/search`,
          { query: trimmedQuery, limit: pageSize },
        ] as const)
      : ([
          `/api/v1/organizations/${orgId}/clients/${clientId}/products`,
          { limit: pageSize, productType },
        ] as const),
    enabled: enabled && !!orgId && !!clientId,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      isSearching
        ? v1ProductControllerSearchProducts(
            orgId,
            clientId,
            { query: trimmedQuery, limit: pageSize, offset: pageParam },
            undefined,
            signal,
          )
        : v1ProductControllerListProducts(
            orgId,
            clientId,
            {
              limit: pageSize,
              offset: pageParam,
              ...(productType && { productType }),
            },
            undefined,
            signal,
          ),
    // A short page means the catalog is exhausted. Next offset is the running
    // row count rather than page*pageSize, so it stays correct even if a page
    // comes back short of what was asked for.
    getNextPageParam: (lastPage, allPages) => {
      if ((lastPage?.data?.length ?? 0) < pageSize) return undefined;
      return allPages.reduce((total, page) => total + (page?.data?.length ?? 0), 0);
    },
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const products = useMemo(() => {
    const rows = data?.pages.flatMap((page) => page?.data ?? []) ?? [];
    // Offsets shift under us when a catalog sync writes mid-scroll, which
    // repeats a row across two pages and collides React keys. Cheap to guard.
    const seen = new Set<string>();
    return rows.filter((row: ProductVariantSearchResultDto) => {
      if (seen.has(row.variantId)) return false;
      seen.add(row.variantId);
      return true;
    });
  }, [data]);

  // Callback ref rather than useRef: the effect below needs to re-run when the
  // sentinel node mounts, and a ref object mutating does not trigger that.
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!sentinel || !hasNextPage || isFetchingNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '240px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // Re-observing after each fetch settles is what chains the pages: a fresh
    // observe() re-evaluates intersection immediately, so a sentinel still on
    // screen (short results, tall viewport) keeps loading instead of stalling
    // waiting for a crossing event that already happened.
  }, [sentinel, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    products,
    isSearching,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef: setSentinel,
  };
}
